import {
  ArrowRight,
  CircleDot,
  CloudSnow,
  Droplets,
  ExternalLink,
  GlassWater,
  Mountain,
  Network,
  ShieldCheck,
  Waves
} from "lucide-react";
import { mineralProfile } from "../data/content";
import { toIntlLocale, useI18n } from "../i18n";
import { TopographicLines } from "./TopographicLines";

const dubaiPathIcons = [Waves, Droplets, ShieldCheck, Network] as const;
const borsecPathIcons = [CloudSnow, Mountain, CircleDot, GlassWater] as const;

function OriginPath({
  title,
  items,
  accent
}: {
  title: string;
  items: readonly {
    label: string;
    Icon: (typeof dubaiPathIcons)[number];
  }[];
  accent?: boolean;
}) {
  return (
    <div className={`origin-path${accent ? " origin-path--accent" : ""}`}>
      <p className="origin-path__title">{title}</p>
      <div className="origin-path__steps">
        {items.map(({ label, Icon }, index) => (
          <div className="origin-step-wrap" key={label}>
            <div className="origin-step">
              <Icon aria-hidden="true" />
              <span>{label}</span>
            </div>
            {index < items.length - 1 ? (
              <ArrowRight className="origin-step__arrow" aria-hidden="true" />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export function WaterOrigin() {
  const { locale, messages } = useI18n();
  const dubaiPath = dubaiPathIcons.map((Icon, index) => ({
    Icon,
    label: messages.water.dubaiPath[index] ?? ""
  }));
  const borsecPath = borsecPathIcons.map((Icon, index) => ({
    Icon,
    label: messages.water.borsecPath[index] ?? ""
  }));

  return (
    <section id="water" className="water-origin section">
      <div className="water-origin__intro">
        <TopographicLines className="water-origin__contours" />
        <div className="shell water-origin__intro-inner">
          <div>
            <h2>{messages.water.introTitle}</h2>
            <p>{messages.water.introBody}</p>
          </div>
          <aside className="dewa-stat" aria-label={messages.water.desalinationFactLabel}>
            <strong>
              <bdi dir="ltr">495</bdi>
            </strong>
            <span>{messages.water.statUnit}</span>
            <p>{messages.water.statBody}</p>
            <a
              href="https://www.dewa.gov.ae/-/media/Files/Sustainability/Sustainability_Report_English_2025.ashx"
              target="_blank"
              rel="noreferrer"
            >
              {messages.water.dewaReport}
              <ExternalLink size={15} aria-hidden="true" />
            </a>
          </aside>
        </div>
      </div>

      <div className="shell origin-comparison">
        <OriginPath title={messages.water.dubaiPathTitle} items={dubaiPath} />
        <OriginPath title={messages.water.borsecPathTitle} items={borsecPath} accent />
        <p className="origin-note">{messages.water.originNote}</p>
      </div>

      <div className="shell mineral-section">
        <div className="mineral-section__copy">
          <h2>{messages.water.mineralsTitle}</h2>
          <p>{messages.water.mineralsBody}</p>
          <div className="mineral-bars" aria-label={messages.water.mineralAnalysisLabel}>
            {mineralProfile.map((item) => (
              <div className="mineral-row" key={item.label}>
                <span className="mineral-row__symbol i18n-ltr">{item.symbol}</span>
                <span className="mineral-row__label">
                  {messages.water.mineralLabels[item.label] ?? item.label}
                </span>
                <span className="mineral-row__track" aria-hidden="true">
                  <span style={{ width: `${Math.max(8, (item.value / 1696) * 100)}%` }} />
                </span>
                <strong>
                  <bdi dir="ltr">
                    {item.value.toLocaleString(toIntlLocale(locale))}{" "}
                    <small>{item.unit}</small>
                  </bdi>
                </strong>
              </div>
            ))}
          </div>
          <p className="source-note">{messages.water.sourceNote}</p>
          <a
            className="text-link"
            href="https://romaqua-group.ro/en/brands/borsec/"
            target="_blank"
            rel="noreferrer"
          >
            {messages.water.sourceLink}
            <ExternalLink size={16} aria-hidden="true" />
          </a>
        </div>
        <figure className="mineral-section__media">
          <img
            src="/assets/images/mineral-water-glass.jpg"
            alt={messages.water.imageAlt}
            width="1448"
            height="1086"
            loading="lazy"
          />
        </figure>
      </div>
    </section>
  );
}
